import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { getDoctorColor } from './calendarUtils';
import { useTranslation } from 'react-i18next';

const BRAND = '#1D2C7B';
const UNASSIGNED_KEY = 'unassigned';

export default function TodayDoctorsMiniSidebar({ events, doctors, colorMap }) {
    const { t } = useTranslation();
    const { rows, total } = useMemo(() => {
        const todayEvents = events.filter((e) => dayjs(e.date).isSame(dayjs(), 'day'));
        const counts = new Map();

        doctors.forEach((d) => counts.set(d.id, 0));
        let unassigned = 0;

        todayEvents.forEach((ev) => {
            if (ev.doctor_id == null) {
                unassigned += 1;
            } else {
                counts.set(ev.doctor_id, (counts.get(ev.doctor_id) ?? 0) + 1);
            }
        });

        const doctorRows = doctors.map((d) => ({
            key: d.id,
            name: d.name ?? t('common.dash'),
            count: counts.get(d.id) ?? 0,
            accent: getDoctorColor(d.id, colorMap),
        }));

        doctorRows.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'uz'));

        if (unassigned > 0) {
            doctorRows.push({
                key: UNASSIGNED_KEY,
                name: t('calendar.unassigned'),
                count: unassigned,
                accent: '#94a3b8',
            });
        }

        return { rows: doctorRows, total: todayEvents.length };
    }, [events, doctors, colorMap, t]);

    return (
        <aside className="flex h-full max-h-full min-h-0 w-full flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white">
            <div
                className="shrink-0 px-3 py-2 text-sm font-semibold text-white"
                style={{ backgroundColor: BRAND }}
            >
                {t('calendar.todayVisits')}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-2 py-2">
                {doctors.length === 0 && total === 0 ? (
                    <p className="m-0 px-1 py-2 text-xs text-slate-500">{t('calendar.noDoctorsList')}</p>
                ) : (
                    <ul className="m-0 list-none space-y-0.5 p-0">
                        {rows.map((row) => (
                            <li
                                key={row.key}
                                className="flex min-h-[32px] items-center gap-2 rounded-md px-1.5 py-1.5"
                            >
                                <span
                                    className="h-2 w-2 shrink-0 rounded-full"
                                    style={{ backgroundColor: row.accent }}
                                    aria-hidden
                                />
                                <span className="min-w-0 flex-1 truncate text-sm leading-tight text-slate-700">
                                    {row.name}
                                </span>
                                <span
                                    className={`shrink-0 text-xs font-semibold tabular-nums ${
                                        row.count > 0 ? 'text-[#1D2C7B]' : 'text-slate-300'
                                    }`}
                                >
                                    {row.count}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}

                {total === 0 && doctors.length > 0 && (
                    <p className="m-0 mt-1 py-3 text-center text-xs text-slate-400">
                        {t('calendar.noTodayVisits')}
                    </p>
                )}
            </div>
        </aside>
    );
}
